import '@/assets/styles/components/meet-provider-test.scss';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { useLocation, useNavigate } from 'react-router';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useApi } from '@/api/useApi.tsx';
import { parseJwt } from '@/utility';
import { WithLandingHeader } from '@/router/navigation-path.ts';
import { useSizes } from '@/utility/responsive.ts';
import { TimeSpan } from '@/utility/time-span.ts';

type RefType = {
	onStartTest: () => void;
};

type Props = {
	src: string;
};

export const MeetProviderTestActions = {
	onReviewCompleted: 'onReviewCompleted',
	onReviewNext: 'onReviewNext',
	initialize: 'initialize'
} as const;

type MeetVWActionsType = (typeof MeetProviderTestActions)[keyof typeof MeetProviderTestActions];

const MeetProviderTestIframe = forwardRef<RefType, Props>(({ src }, ref) => {
	const { ExpertControllerApi } = useApi();
	const location = useLocation();
	const navigate = useNavigate();
	const loadEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const { isMobile } = useSizes();

	const { data: authData } = useAuthStore();

	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	const isApplicant = useMemo(() => {
		if (authData?.access_token) {
			return parseJwt(authData?.access_token)?.resource_access?.['live-expert-applicant']?.roles?.includes('Expert_Applicant');
		}

		return false;
	}, [authData?.access_token]);

	const isLandingPage = useMemo(() => WithLandingHeader[location.pathname as keyof typeof WithLandingHeader], [location.pathname]);

	const { data: expertDetailInfo } = useQueryProvider([QUERY_KEYS.getLoggedInExpertDetailInfo], () => ExpertControllerApi.getLoggedInExpertDetailInfo(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		enabled: !isApplicant && !isLandingPage
	});

	const onReviewCompleted = () => {
		if (window.history.state && window.history.state.idx > 0) {
			navigate(-1);
		} else {
			navigate('/', { replace: true });
		}
	};

	const onMessage = (event: MessageEvent): void => {
		const data = event?.data;

		if (data?.type === MeetProviderTestActions.onReviewCompleted) {
			onReviewCompleted();
		} else if (data?.type === MeetProviderTestActions.onReviewNext) {
			return;
		}
	};

	const sendMessage = (payload: { type: MeetVWActionsType; data?: any }): void => {
		iframeRef.current?.contentWindow?.postMessage(payload, '*');
		(window as any).iframeWindow = iframeRef.current?.contentWindow;
	};

	const onLoadEnd = () => {
		if (loadEndTimeoutRef.current) clearTimeout(loadEndTimeoutRef.current);

		const data = {
			isStudent: false,
			name: `${expertDetailInfo?.expert?.name || ''} ${expertDetailInfo?.expert?.surname || ''}`,
			isMobile: isMobile,
			bearerToken: authData?.access_token
		};

		loadEndTimeoutRef.current = setTimeout(() => {
			sendMessage({
				type: MeetProviderTestActions.initialize,
				data: data
			});
		}, TimeSpan.milliSecond.sixHundred);
	};

	const onInit = (): void => {
		if (iframeRef.current) {
			iframeRef.current.onload = () => onLoadEnd();
		}
	};

	useEffect(() => {
		iframeRef.current = document.getElementById('meet-iframe') as HTMLIFrameElement;

		if (authData?.access_token) {
			onInit();
			window.addEventListener('message', onMessage);
		}

		return () => {
			window.removeEventListener('message', onMessage);
		};
	}, [authData?.access_token]);

	return (
		<div className={'meet-provider-iframe-container'}>
			<iframe
				src={src}
				id="meet-iframe"
				className="meet-iframe"
				allow="autoplay; camera; microphone; display-capture"
				allowFullScreen
			/>
		</div>
	);
});

export default MeetProviderTestIframe;
